/**
 * Particle helpers for the bats effect: flock creation and an advance-and-draw
 * step. Bats fly mostly horizontally in mixed directions on a gentle sine bob,
 * flapping their wings, and respawn on the opposite edge when they exit.
 */

/** A single bat's position, motion, and wing-flap state. */
export interface Bat {
  x: number;
  baseY: number;
  vx: number; // signed horizontal speed (px/frame); sign = direction
  size: number; // rendered width in px
  phase: number; // vertical-bob phase
  freq: number; // vertical-bob speed
  amp: number; // vertical-bob amplitude (px)
  flap: number; // wing-flap phase
  flapSpeed: number;
}

const rand = (min: number, max: number): number => min + Math.random() * (max - min);

// Bat silhouette, drawn in a 100×56 box. Built lazily (Path2D doesn't exist in
// the jsdom test environment, and this module is imported via the registry).
const BAT_PATH_D =
  'M50,30 C46,12 36,20 31,9 C33,24 19,21 7,31 C22,28 31,41 50,33 C69,41 78,28 93,31 C81,21 67,24 69,9 C64,20 54,12 50,30 Z';
const BAT_W = 100;
const BAT_H = 56;
let batPath: Path2D | undefined;

/** Build a small flock spread across the upper screen, in mixed directions. */
export function createBats(count: number, width: number, height: number): Bat[] {
  return Array.from({ length: count }, () => ({
    x: rand(0, width),
    baseY: rand(height * 0.06, height * 0.55),
    vx: (Math.random() < 0.5 ? -1 : 1) * rand(3.2, 6.5),
    size: rand(30, 58),
    phase: rand(0, Math.PI * 2),
    freq: rand(0.03, 0.06),
    amp: rand(8, 22),
    flap: rand(0, Math.PI * 2),
    flapSpeed: rand(0.4, 0.7),
  }));
}

/**
 * Advance and paint every bat one frame. `fill` is a CSS color string (resolved
 * from the `--bat-fill` theme var). Mutates `bats` in place.
 */
export function drawBats(
  ctx: CanvasRenderingContext2D,
  bats: Bat[],
  width: number,
  height: number,
  fill: string
): void {
  batPath ??= new Path2D(BAT_PATH_D);
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = fill;
  for (const bat of bats) {
    bat.x += bat.vx;
    bat.phase += bat.freq;
    bat.flap += bat.flapSpeed;
    const y = bat.baseY + Math.sin(bat.phase) * bat.amp;

    const half = bat.size / 2;
    if (bat.vx > 0 && bat.x - half > width) {
      bat.x = -half;
      bat.baseY = rand(height * 0.06, height * 0.55);
    } else if (bat.vx < 0 && bat.x + half < 0) {
      bat.x = width + half;
      bat.baseY = rand(height * 0.06, height * 0.55);
    }

    // Wings flap by squashing the silhouette horizontally.
    const flapScale = 0.4 + 0.6 * Math.abs(Math.cos(bat.flap));
    const scale = bat.size / BAT_W;
    ctx.save();
    ctx.translate(bat.x, y);
    ctx.scale(scale * flapScale, scale);
    ctx.translate(-BAT_W / 2, -BAT_H / 2);
    ctx.fill(batPath);
    ctx.restore();
  }
}
